import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CalendarDays, CheckCircle2, Clock3, Mail, Plus, Save, XCircle } from 'lucide-react';
import { Editor } from '@tinymce/tinymce-react';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/models/dom';
import 'tinymce/themes/silver';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/code';
import 'tinymce/plugins/image';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/table';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/content/default/content.min.css';
import {
  createMonthlyImpactCampaign,
  getMonthlyImpactCampaign,
  listCampaignRecipientStatuses,
  listCampaignRuns,
  listMonthlyImpactCampaigns,
  updateMonthlyImpactCampaign,
  type MonthlyImpactCampaign,
  type MonthlyImpactCampaignPayload,
  type MonthlyImpactCampaignRun,
  type MonthlyImpactCampaignStatus,
  type MonthlyImpactRecipientStatusRow,
} from '@/lib/monthly-impact-campaigns';
import {
  FIRESTORE_INDEX_BUILDING_MESSAGE,
  getFirestoreIndexStatusMessage,
  isFirestoreIndexBuilding,
  tinymceEmailEditorInit,
} from '@/lib/tinymce-email-editor-init';

interface AdminMonthlyImpactCampaignsProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
}

type View = 'list' | 'create' | 'edit';

interface CampaignFormState {
  monthKey: string;
  title: string;
  subject: string;
  preheader: string;
  heroStoryHtml: string;
  heroStoryText: string;
  impactSummary: string;
  status: MonthlyImpactCampaignStatus;
  scheduledAt: string;
}

const EMPTY_FORM: CampaignFormState = {
  monthKey: '',
  title: '',
  subject: '',
  preheader: '',
  heroStoryHtml: '',
  heroStoryText: '',
  impactSummary: '',
  status: 'draft',
  scheduledAt: '',
};

function toDateTimeInput(value: unknown): string {
  if (!value) return '';
  const date =
    typeof value === 'object' && value !== null && 'toDate' in value
      ? (value as { toDate: () => Date }).toDate()
      : (value as Date);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function fromCampaignToForm(campaign: MonthlyImpactCampaign): CampaignFormState {
  return {
    monthKey: campaign.monthKey,
    title: campaign.title,
    subject: campaign.subject,
    preheader: campaign.preheader,
    heroStoryHtml: campaign.heroStoryHtml,
    heroStoryText: campaign.heroStoryText,
    impactSummary: campaign.impactSummary,
    status: campaign.status,
    scheduledAt: toDateTimeInput(campaign.scheduledAt),
  };
}

function getStatusBadgeClass(status: MonthlyImpactCampaignStatus): string {
  if (status === 'sent') return 'bg-green-100 text-green-700';
  if (status === 'scheduled' || status === 'approved') return 'bg-blue-100 text-blue-700';
  if (status === 'sending') return 'bg-purple-100 text-purple-700';
  if (status === 'failed' || status === 'cancelled') return 'bg-red-100 text-red-700';
  return 'bg-yellow-100 text-yellow-700';
}

function formatStatusLabel(status: MonthlyImpactCampaignStatus): string {
  if (status === 'approved') return 'Approved';
  if (status === 'scheduled') return 'Scheduled';
  if (status === 'cancelled') return 'Cancelled';
  if (status === 'sending') return 'Sending';
  if (status === 'sent') return 'Sent';
  if (status === 'failed') return 'Failed';
  return 'Draft';
}

function getSaveSuccessMessage(
  status: MonthlyImpactCampaignStatus,
  isCreate: boolean
): string {
  if (isCreate) {
    return 'Monthly impact campaign created.';
  }
  if (status === 'approved') {
    return 'Campaign approved. Schedule it when you are ready to send.';
  }
  if (status === 'scheduled') {
    return 'Campaign scheduled. It will be picked up by the monthly scheduler.';
  }
  if (status === 'cancelled') {
    return 'Campaign cancelled.';
  }
  return 'Monthly impact campaign saved.';
}

export function AdminMonthlyImpactCampaigns({
  currentUserId,
  showNotify,
  confirmAction,
}: AdminMonthlyImpactCampaignsProps): JSX.Element {
  const [view, setView] = useState<View>('list');
  const [campaigns, setCampaigns] = useState<MonthlyImpactCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<MonthlyImpactCampaign | null>(null);
  const [form, setForm] = useState<CampaignFormState>(EMPTY_FORM);
  const [runs, setRuns] = useState<MonthlyImpactCampaignRun[]>([]);
  const [recipientRows, setRecipientRows] = useState<MonthlyImpactRecipientStatusRow[]>([]);
  const [recipientCursor, setRecipientCursor] = useState<unknown>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingMoreRecipients, setLoadingMoreRecipients] = useState(false);

  const notify = useCallback(
    (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      showNotify?.(message, type);
    },
    [showNotify]
  );

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listMonthlyImpactCampaigns();
      setCampaigns(list);
    } catch (error) {
      console.error('[AdminMonthlyImpactCampaigns] Failed to load campaigns:', error);
      notify('Failed to load monthly impact campaigns.', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  const openCreate = (): void => {
    setSelectedCampaign(null);
    setRuns([]);
    setRecipientRows([]);
    setRecipientCursor(null);
    setForm(EMPTY_FORM);
    setView('create');
  };

  const openEdit = async (campaign: MonthlyImpactCampaign): Promise<void> => {
    setSelectedCampaign(campaign);
    setForm(fromCampaignToForm(campaign));
    setView('edit');
    setRuns([]);
    setRecipientRows([]);
    setRecipientCursor(null);
    setLoadingDetail(true);

    let indexStillBuilding = false;

    try {
      const runList = await listCampaignRuns(campaign.id);
      setRuns(runList);
    } catch (error) {
      console.error('[AdminMonthlyImpactCampaigns] Failed to load campaign runs:', error);
      if (isFirestoreIndexBuilding(error)) {
        indexStillBuilding = true;
      } else {
        const indexMessage = getFirestoreIndexStatusMessage(error);
        notify(indexMessage ?? 'Failed to load campaign run history.', 'error');
      }
    }

    try {
      const recipientPage = await listCampaignRecipientStatuses(campaign.id, 25);
      setRecipientRows(recipientPage.rows);
      setRecipientCursor(recipientPage.cursor);
    } catch (error) {
      console.error('[AdminMonthlyImpactCampaigns] Failed to load recipient statuses:', error);
      if (isFirestoreIndexBuilding(error)) {
        indexStillBuilding = true;
      } else {
        const indexMessage = getFirestoreIndexStatusMessage(error);
        notify(indexMessage ?? 'Failed to load recipient statuses.', 'error');
      }
    }

    if (indexStillBuilding) {
      notify(FIRESTORE_INDEX_BUILDING_MESSAGE, 'info');
    }

    setLoadingDetail(false);
  };

  const loadMoreRecipients = async (): Promise<void> => {
    if (!selectedCampaign || !recipientCursor) return;
    setLoadingMoreRecipients(true);
    try {
      const next = await listCampaignRecipientStatuses(
        selectedCampaign.id,
        25,
        recipientCursor as never
      );
      setRecipientRows((prev) => [...prev, ...next.rows]);
      setRecipientCursor(next.cursor);
    } catch (error) {
      console.error('[AdminMonthlyImpactCampaigns] Failed to load more recipients:', error);
      notify('Unable to load additional recipient statuses.', 'error');
    } finally {
      setLoadingMoreRecipients(false);
    }
  };

  const goToList = (): void => {
    setView('list');
    setSelectedCampaign(null);
    setRuns([]);
    setRecipientRows([]);
    setRecipientCursor(null);
    setForm(EMPTY_FORM);
  };

  const validateForm = (): boolean => {
    if (!form.monthKey.trim()) return false;
    if (!form.title.trim()) return false;
    if (!form.subject.trim()) return false;
    if (!form.heroStoryHtml.trim()) return false;
    if (!form.impactSummary.trim()) return false;
    return true;
  };

  const buildPayload = (nextStatus?: MonthlyImpactCampaignStatus): MonthlyImpactCampaignPayload => {
    const normalizedStatus = nextStatus ?? form.status;
    const approvedAt = normalizedStatus === 'approved' || normalizedStatus === 'scheduled' ? new Date() : null;
    const approvedBy = approvedAt ? currentUserId : null;
    return {
      monthKey: form.monthKey.trim(),
      title: form.title.trim(),
      subject: form.subject.trim(),
      preheader: form.preheader.trim(),
      heroStoryHtml: form.heroStoryHtml,
      heroStoryText: form.heroStoryText.trim(),
      impactSummary: form.impactSummary.trim(),
      status: normalizedStatus,
      scheduledAt: form.scheduledAt ? new Date(form.scheduledAt) : null,
      approvedAt,
      approvedBy,
    };
  };

  const saveCampaign = async (nextStatus?: MonthlyImpactCampaignStatus): Promise<void> => {
    if (!validateForm()) {
      notify('Complete required fields before saving the campaign.', 'error');
      return;
    }
    setSaving(true);
    const payload = buildPayload(nextStatus);
    const resultingStatus = payload.status;
    try {
      if (selectedCampaign) {
        await updateMonthlyImpactCampaign(selectedCampaign.id, payload, currentUserId);
        const refreshed = await getMonthlyImpactCampaign(selectedCampaign.id);
        if (refreshed) {
          setSelectedCampaign(refreshed);
          setForm(fromCampaignToForm(refreshed));
        } else {
          setForm((prev) => ({ ...prev, status: resultingStatus }));
        }
        notify(getSaveSuccessMessage(resultingStatus, false), 'success');
      } else {
        const id = await createMonthlyImpactCampaign(payload, currentUserId);
        notify(getSaveSuccessMessage(resultingStatus, true), 'success');
        const created = await getMonthlyImpactCampaign(id);
        if (created) {
          setSelectedCampaign(created);
          setForm(fromCampaignToForm(created));
          setView('edit');
        } else {
          goToList();
        }
      }
      await loadCampaigns();
    } catch (error) {
      console.error('[AdminMonthlyImpactCampaigns] Failed to save campaign:', error);
      notify('Failed to save campaign.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelCampaign = (): void => {
    if (!selectedCampaign) return;
    const action = async (): Promise<void> => {
      await saveCampaign('cancelled');
    };
    if (confirmAction) {
      confirmAction(
        'Cancel campaign?',
        'This sets the campaign status to cancelled and removes it from scheduler pickup.',
        () => {
          void action();
        }
      );
      return;
    }
    void action();
  };

  const workflowStatus = form.status;
  const canApprove = workflowStatus === 'draft';
  const canSchedule = workflowStatus === 'draft' || workflowStatus === 'approved';
  const canCancel = !['sent', 'cancelled', 'sending'].includes(workflowStatus);

  const campaignStats = useMemo(() => {
    return campaigns.reduce(
      (acc, campaign) => {
        if (campaign.status === 'scheduled') acc.scheduled += 1;
        if (campaign.status === 'sending') acc.sending += 1;
        if (campaign.status === 'sent') acc.sent += 1;
        return acc;
      },
      { scheduled: 0, sending: 0, sent: 0 }
    );
  }, [campaigns]);

  if (view === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Monthly Impact Campaigns</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Create, approve, schedule, and audit CF-10 donor impact email campaigns.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            New Campaign
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Scheduled</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{campaignStats.scheduled}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sending</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{campaignStats.sending}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sent</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{campaignStats.sent}</p>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <div className="p-10 text-center">
              <Mail size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-600">No monthly impact campaigns yet.</p>
              <button
                type="button"
                onClick={openCreate}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Plus size={15} />
                Create your first campaign
              </button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Month</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Scheduled</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">{campaign.monthKey}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{campaign.title}</p>
                      <p className="text-xs text-gray-500">{campaign.subject}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getStatusBadgeClass(campaign.status)}`}>
                        {formatStatusLabel(campaign.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {campaign.scheduledAt ? toDateTimeInput(campaign.scheduledAt).replace('T', ' ') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          void openEdit(campaign);
                        }}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        View details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 left-0 z-30 flex flex-col bg-white lg:left-56">
      <div className="flex items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={goToList}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft size={15} />
            Back to campaigns
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <h2 className="truncate text-base font-semibold text-gray-900">
            {view === 'create' ? 'New Monthly Impact Campaign' : selectedCampaign?.title ?? 'Campaign'}
          </h2>
          {view === 'edit' ? (
            <span
              className={`inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${getStatusBadgeClass(workflowStatus)}`}
            >
              {formatStatusLabel(workflowStatus)}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {selectedCampaign ? (
            <>
              {canApprove ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    void saveCampaign('approved');
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                >
                  <CheckCircle2 size={14} />
                  Approve
                </button>
              ) : null}
              {canSchedule ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    void saveCampaign('scheduled');
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-100 disabled:opacity-60"
                >
                  <Clock3 size={14} />
                  Schedule
                </button>
              ) : null}
              {canCancel ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleCancelCampaign}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
                >
                  <XCircle size={14} />
                  Cancel
                </button>
              ) : null}
            </>
          ) : null}
          <button
            type="button"
            onClick={() => {
              void saveCampaign();
            }}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            <Save size={15} />
            {saving ? 'Saving…' : view === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="flex w-80 shrink-0 flex-col gap-4 overflow-y-auto border-r border-gray-200 bg-gray-50 p-5">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Month Key*
            </label>
            <input
              type="text"
              value={form.monthKey}
              onChange={(event) => setForm((prev) => ({ ...prev, monthKey: event.target.value }))}
              placeholder="2026-06"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Campaign Title*
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Subject*
            </label>
            <input
              type="text"
              value={form.subject}
              onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Preheader
            </label>
            <input
              type="text"
              value={form.preheader}
              onChange={(event) => setForm((prev) => ({ ...prev, preheader: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Schedule (Asia/Manila)
            </label>
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="datetime-local"
                value={form.scheduledAt}
                onChange={(event) => setForm((prev) => ({ ...prev, scheduledAt: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Hero Text Fallback
            </label>
            <textarea
              rows={4}
              value={form.heroStoryText}
              onChange={(event) => setForm((prev) => ({ ...prev, heroStoryText: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Impact Summary*
            </label>
            <textarea
              rows={6}
              value={form.impactSummary}
              onChange={(event) => setForm((prev) => ({ ...prev, impactSummary: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            />
          </div>
        </aside>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Hero Story Body (TinyMCE)
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Email-safe editor profile. Server-side sanitization is still enforced before send.
            </p>
          </div>
          <div className="p-5">
            <Editor
              licenseKey="gpl"
              value={form.heroStoryHtml}
              onEditorChange={(content) => setForm((prev) => ({ ...prev, heroStoryHtml: content }))}
              init={tinymceEmailEditorInit}
            />
          </div>

          {selectedCampaign ? (
            <div className="grid grid-cols-1 gap-4 border-t border-gray-200 p-5 xl:grid-cols-2">
              <section className="rounded-xl border border-gray-200 bg-white">
                <header className="border-b border-gray-100 px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Campaign Runs</h3>
                </header>
                {loadingDetail ? (
                  <div className="p-4 text-sm text-gray-500">Loading run history...</div>
                ) : runs.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No runs yet for this campaign.</div>
                ) : (
                  <div className="max-h-72 overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Started</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Sent/Failed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map((run) => (
                          <tr key={run.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-700">{run.status}</td>
                            <td className="px-3 py-2 text-gray-700">{toDateTimeInput(run.startedAt).replace('T', ' ') || '—'}</td>
                            <td className="px-3 py-2 text-gray-700">
                              {run.sentCount}/{run.failedCount}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-gray-200 bg-white">
                <header className="border-b border-gray-100 px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Recipient Statuses</h3>
                </header>
                {loadingDetail ? (
                  <div className="p-4 text-sm text-gray-500">Loading recipient statuses...</div>
                ) : recipientRows.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No recipient statuses yet for this campaign.</div>
                ) : (
                  <div className="max-h-72 overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Donor</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Attempts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipientRows.map((recipient) => (
                          <tr key={recipient.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 font-mono text-xs text-gray-700">{recipient.donorId}</td>
                            <td className="px-3 py-2 text-gray-700">{recipient.status}</td>
                            <td className="px-3 py-2 text-gray-700">{recipient.attemptCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {recipientCursor ? (
                      <div className="border-t border-gray-100 p-3">
                        <button
                          type="button"
                          onClick={() => {
                            void loadMoreRecipients();
                          }}
                          disabled={loadingMoreRecipients}
                          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                          {loadingMoreRecipients ? 'Loading...' : 'Load more'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}
              </section>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
